import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../components/common/SafeIcon';
import Navbar from '../components/nav/Navbar';
import {
  CHANGELOG_ENTRIES, APP_VERSION, APP_NOW_UTC,
  formatDateTime, formatDate, formatRelative,
} from '../constants/changelog';

const { FiArrowLeft, FiZap, FiGitCommit, FiStar, FiClock, FiRadio } = FiIcons;

const CATEGORY_STYLES = {
  feature:     { label: 'Feature',     color: 'text-brand-pink'  },
  improvement: { label: 'Improvement', color: 'text-sky-400'     },
  fix:         { label: 'Fix',         color: 'text-emerald-400' },
};

// ── Live clock anchored to APP_NOW_UTC ────────────────────
// Ticks every second using real elapsed wall-clock time,
// offset from the app canonical deploy anchor (APP_NOW_UTC).
// When APP_NOW_UTC is updated on each deploy, the clock
// automatically resets to the new anchor and ticks forward.
const useLiveClock = () => {
  const [now, setNow] = useState(APP_NOW_UTC);
  useEffect(() => {
    const mountedAt = Date.now();
    const id = setInterval(() => {
      const elapsed = Date.now() - mountedAt;
      setNow(new Date(APP_NOW_UTC.getTime() + elapsed));
    }, 1000);
    return () => clearInterval(id);
  }, []);
  return now;
};

// ── Single changelog item ─────────────────────────────────
const ChangeItem = ({ item, liveNow }) => {
  const cfg      = CATEGORY_STYLES[item.category] || CATEGORY_STYLES.improvement;
  const itemTime = item.deployedAt ? formatDateTime(item.deployedAt) : null;
  const rel      = item.deployedAt ? formatRelative(item.deployedAt, liveNow) : null;

  return (
    <div className="flex gap-4 py-4 border-b border-white/5 last:border-0">
      <div className="text-lg flex-shrink-0 w-6 text-center mt-0.5">{item.emoji}</div>
      <div className="flex-1 space-y-1.5">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-[12px] font-black text-white uppercase tracking-tight">{item.title}</p>
          <span className={`text-[9px] font-black uppercase tracking-widest ${cfg.color}`}>
            {item.category === 'feature' ? '🚀' : item.category === 'fix' ? '🐛' : '🛠️'} {cfg.label}
          </span>
        </div>
        <p className="text-[11px] font-bold text-zinc-500 leading-relaxed">{item.body}</p>
        {itemTime && (
          <div className="flex items-center gap-1 pt-0.5">
            <SafeIcon icon={FiClock} className="text-zinc-800 text-[8px]" />
            <span className="text-[9px] font-bold text-zinc-700">
              {itemTime}
              {rel && <span className="text-zinc-800 ml-1.5">({rel})</span>}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Version card ──────────────────────────────────────────
const VersionCard = ({ entry, index, isLatest, liveNow }) => {
  const dateTime = formatDateTime(entry.deployedAt);
  const dateOnly = formatDate(entry.deployedAt);
  const rel      = formatRelative(entry.deployedAt, liveNow);

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.08, duration: 0.4, ease: 'easeOut' }}
      className={`glass-card rounded-3xl overflow-hidden ${isLatest ? 'border-brand-pink/20' : ''}`}
    >
      {/* Version header */}
      <div
        className="px-8 py-6 border-b border-white/5 flex items-center justify-between gap-4 flex-wrap"
        style={isLatest ? { background: 'linear-gradient(90deg, rgba(255,102,204,0.06) 0%, transparent 100%)' } : {}}
      >
        <div className="flex items-center gap-4">
          <div className={`w-10 h-10 rounded-2xl flex items-center justify-center border ${
            isLatest
              ? 'bg-brand-pink/15 border-brand-pink/25'
              : 'bg-white/5 border-white/10'
          }`}>
            <SafeIcon
              icon={isLatest ? FiStar : FiGitCommit}
              className={`text-base ${isLatest ? 'text-brand-pink' : 'text-zinc-500'}`}
            />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-black text-white tracking-tight">v{entry.version}</h2>
              {isLatest && (
                <span className="px-2 py-0.5 rounded-full bg-brand-pink/15 border border-brand-pink/25 text-[9px] font-black text-brand-pink uppercase tracking-widest">
                  Latest
                </span>
              )}
              <span className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest border ${
                entry.severity === 'high'
                  ? 'bg-red-500/10 border-red-500/25 text-red-400'
                  : entry.severity === 'medium'
                  ? 'bg-amber-500/10 border-amber-500/25 text-amber-400'
                  : 'bg-zinc-800 border-zinc-700 text-zinc-500'
              }`}>
                {entry.severity}
              </span>
            </div>
            <div className="flex items-center gap-1.5 mt-1">
              <SafeIcon icon={FiClock} className="text-zinc-700 text-[9px]" />
              <span className="text-[10px] font-bold text-zinc-500" title={entry.deployedAt}>
                {dateTime || dateOnly}
                {rel && <span className="text-zinc-700 ml-1.5">({rel})</span>}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 text-[9px] font-black uppercase tracking-widest text-zinc-700">
          <SafeIcon icon={FiZap} className="text-[10px]" />
          {entry.items.length} changes
        </div>
      </div>

      {/* Headline + summary */}
      <div className="px-8 py-5 border-b border-white/5">
        <p className="text-sm font-black text-white">{entry.headline}</p>
        <p className="text-[12px] font-bold text-zinc-500 mt-1 leading-relaxed">{entry.summary}</p>
      </div>

      {/* Items */}
      <div className="px-8 py-4">
        {entry.items.map((item) => (
          <ChangeItem key={item.title} item={item} liveNow={liveNow} />
        ))}
      </div>
    </motion.div>
  );
};

// ── Page ──────────────────────────────────────────────────
const Changelog = () => {
  const liveNow = useLiveClock();

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <Navbar />

      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="ambient-orb-pink w-[500px] h-[500px] top-[-10%] right-[-10%] animate-caustic" />
      </div>

      <div className="relative z-10 max-w-2xl mx-auto px-6 pt-32 pb-24 space-y-8">

        {/* Page header */}
        <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
          <Link to="/" className="flex items-center gap-2 text-zinc-500 hover:text-white transition-colors group w-fit">
            <div className="w-8 h-8 rounded-xl glass-card flex items-center justify-center group-hover:border-brand-pink/30 transition-all">
              <SafeIcon icon={FiArrowLeft} className="text-sm" />
            </div>
            <span className="text-xs font-black uppercase tracking-widest">Back</span>
          </Link>

          <div className="space-y-2">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-xs font-black tracking-widest text-brand-pink uppercase">Platform Updates</span>
              <span className="px-2 py-0.5 rounded-full glass-card text-[9px] font-black text-zinc-500">
                Current: v{APP_VERSION}
              </span>
              {/* Live clock pill — ticks from APP_NOW_UTC */}
              <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-full glass-card text-[9px] font-black text-zinc-500">
                <SafeIcon icon={FiRadio} className="text-emerald-500 text-[9px]" />
                {formatDateTime(liveNow)}
              </span>
            </div>
            <h1 className="text-5xl font-black tracking-tighter uppercase text-white">Changelog</h1>
            <p className="text-zinc-500 font-bold text-sm leading-relaxed">
              A human-readable history of everything shipped — features, improvements, and fixes.
              All times shown in Eastern Time (EDT).
            </p>
          </div>
        </motion.div>

        {/* Version cards */}
        <div className="space-y-6">
          {CHANGELOG_ENTRIES
            .filter(e => !e.silent)
            .map((entry, idx) => (
              <VersionCard
                key={entry.version}
                entry={entry}
                index={idx}
                isLatest={idx === 0}
                liveNow={liveNow}
              />
            ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="text-center text-[10px] font-black uppercase tracking-widest text-zinc-800 pt-4"
        >
          Vision Direct · All times in Eastern Time (EDT)
        </motion.p>
      </div>
    </div>
  );
};

export default Changelog;